/**
 * pendingReevaluateStore — typed store for the "save this reevaluated trip
 * after login" blob (H-215, Phase 3 of sprint H-PINREEVAL).
 *
 * Mirrors pendingSaveStore.ts in shape and intent. The anonymous viewer of a
 * shared trip taps Reevaluate, sees a personalised re-rank in-place, then
 * taps "Save this trip". We stash the original share slug + reevaluation
 * context + the reevaluated places into localStorage, open the AuthModal,
 * and after sign-in/up the App-level materialiser drains this store and
 * inserts a fresh owned itinerary for the new user.
 */

import { safeStorage } from './safeStorage';
import type { ReevaluatedPlace, ReevaluateContext } from '@/services/reevaluate';

const STORAGE_KEY = 'tb_pending_reevaluate';

export interface PendingReevaluatePayload {
  shareSlug: string;
  context: ReevaluateContext;
  reevaluated: ReevaluatedPlace[];
  cachedAt: number;
  v: 1;
}

export function readPendingReevaluate(): PendingReevaluatePayload | null {
  const raw = safeStorage.getJSON<PendingReevaluatePayload>(STORAGE_KEY);
  if (!raw) return null;
  if (raw.v !== 1) {
    safeStorage.remove(STORAGE_KEY);
    return null;
  }
  if (!raw.shareSlug || !raw.context || !Array.isArray(raw.reevaluated)) {
    safeStorage.remove(STORAGE_KEY);
    return null;
  }
  return raw;
}

export function writePendingReevaluate(
  shareSlug: string,
  context: ReevaluateContext,
  reevaluated: ReevaluatedPlace[],
): boolean {
  const payload: PendingReevaluatePayload = {
    shareSlug,
    context,
    reevaluated,
    cachedAt: Date.now(),
    v: 1,
  };
  return safeStorage.setJSON(STORAGE_KEY, payload);
}

export function clearPendingReevaluate(): void {
  safeStorage.remove(STORAGE_KEY);
}
